import { useEffect, useRef } from "react";
import { isMonitoringSensorId } from "./installationInfo";

/** Backend opens an arc log when arc_level > 1 and clears it when it drops back. */
const ARC_LOG_LEVEL = 1;
/** Give the backend time to write the log row before reading it. */
const REFRESH_DELAY_MS = 3000;

const alarmingSensorKey = (devices, isAlarming) =>
  (devices || [])
    .flatMap((device) => {
      const serial = device?.device_info?.serial_number || "";
      return (device?.sensors || [])
        .filter(
          (s) => isMonitoringSensorId(s?.sensor_id) && Number(s?.com_state) === 1 && isAlarming(s)
        )
        .map((s) => `${serial}:${s.sensor_id}`);
    })
    .sort()
    .join(",");

/** Sensors currently in arc alarm, e.g. "26200001:1,26200001:4". */
export const arcAlarmKey = (devices) =>
  alarmingSensorKey(
    devices,
    (s) => Number(s.arc_level_raw ?? s.arc_level) > ARC_LOG_LEVEL || Number(s.arc_state) === 1
  );

/** Sensors currently above their temperature threshold (open temp cycle). */
export const tempAlarmKey = (devices) =>
  alarmingSensorKey(devices, (s) => Number(s.temp_state) === 1);

/**
 * Run `load` once when enabled, then again only when `alarmKey` changes
 * (an alarm starts or clears) instead of polling.
 */
export const useAlarmTriggeredFetch = (enabled, alarmKey, load) => {
  const loadRef = useRef(load);
  loadRef.current = load;
  const loaded = useRef(false);

  useEffect(() => {
    if (!enabled) {
      loaded.current = false;
      return undefined;
    }
    if (!loaded.current) {
      loaded.current = true;
      loadRef.current();
      return undefined;
    }
    const id = setTimeout(() => loadRef.current(), REFRESH_DELAY_MS);
    return () => clearTimeout(id);
  }, [enabled, alarmKey]);
};
